import { type TOC } from '@ember/component/template-only';
import { modifier } from 'ember-modifier';

export interface S {
  Element: HTMLDivElement;
  Args: {
    // See https://steveholgado.com/typescript-types-from-arrays to understand this type
    variant?: (typeof VALID_VARIANTS)[number];
    size?: (typeof VALID_SIZES)[number];
  };
  Blocks: { default: []; value: []; secondary: [] };
}

export const VALID_VARIANTS = [
  'inverted-pink',
  'pink',
  'purple',
  'grey'
] as const; // See [const assertions](https://www.typescriptlang.org/docs/handbook/release-notes/typescript-3-4.html#const-assertions) to know why we need `as const`

export const VALID_SIZES = ['single-line', 'double-line'] as const;

const STYLES = {
  base: [
    'rounded-[42px]',
    'px-5',
    'inline-flex',
    'flex-col',
    'justify-center',
    'items-end',
    'text-p1'
  ],
  pink: [
    'bg-pink-light',
    'text-pink',
    '[&>span:nth-child(2)]:text-pink-medium'
  ],
  'inverted-pink': [
    'bg-pink',
    'text-white',
    '[&>span:nth-child(2)]:text-pink-light'
  ],
  purple: [
    'bg-purple-light',
    'text-purple',
    '[&>span:nth-child(2)]:text-purple-medium'
  ],
  grey: [
    'bg-grey-light',
    'text-grey',
    '[&>span:nth-child(2)]:text-grey-medium'
  ],
  'single-line': ['py-1'],
  'double-line': ['py-4']
} as const;

function variant(v: S['Args']['variant']) {
  return v || 'pink';
}

function size(s: S['Args']['size']) {
  return s || 'double-line';
}

function styles(v: S['Args']['variant'], s: S['Args']['size']) {
  return [...STYLES.base, ...STYLES[variant(v)], ...STYLES[size(s)]].join(' ');
}

const registerSecondary = modifier((el: HTMLElement) => {
  (el.parentElement as S['Element']).classList.replace('py-4', 'py-2');
});

const WrPillComponent: TOC<S> = <template>
  <div
    class={{styles @variant @size}}
    data-test-wr-pill-variant={{variant @variant}}
    ...attributes
  >
    {{#if (has-block 'value')}}
      <span>{{yield to='value'}}</span>
      <span {{registerSecondary}}>{{yield to='secondary'}}</span>
    {{else}}
      <span>{{yield}}</span>
    {{/if}}
  </div>
</template>;

export default WrPillComponent;
